import { gql } from '@apollo/client'

export const CONTENT_BLOCK_PREVIEW_FRAGMENT = gql`
  fragment ContentBlockPreviewFields on ContentBlock {
    id
    type
    style
    background_color
    text_color
    content_block_profiles {
      id
      name
      description
      description_format
      language
    }
    main_image {
      id
      src
    }
    gallery_images {
      id
      src
    }
    config
    product_containers {
      id
    }
    grid_product_containers {
      id
    }
    categories {
      id
      image {
        id
        src
      }
      banners {
        id
        src
      }
      category_profiles {
        id
        name
        language
      }
    }
  }
`

// Response selection for the CUSTOM content-block mutations
// (createNewContentBlock / updateExistingContentBlock). These resolvers
// re-fetch the block with a FIXED Prisma `include` that covers
// content_block_profiles, product_containers, main_image, gallery_images and
// linked_items — but NOT `categories` (which is non-nullable `[Category!]!`
// in the schema). Selecting `categories` here makes GraphQL fail to serialize
// the response ("Cannot return null for non-nullable field
// ContentBlock.categories"). So this fragment intentionally OMITS `categories`
// (and any other relation the resolver doesn't include). The shared
// CONTENT_BLOCK_PREVIEW_FRAGMENT keeps `categories` because it is only used by
// query-side selections (GET_PAGE / GET_ALL_CONTENT_BLOCKS) that resolve
// through the standard CRUD/Page resolvers, which DO resolve categories.
export const CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT = gql`
  fragment ContentBlockMutationResult on ContentBlock {
    id
    type
    style
    arrangement
    background_color
    text_color
    config
    video_type
    video_link
    content_block_profiles {
      id
      name
      description
      description_format
      link
      language
    }
    main_image {
      id
      src
    }
    gallery_images {
      id
      src
    }
    product_containers {
      id
    }
    grid_product_containers {
      id
    }
  }
`

export const GET_CONTENT_BLOCKS = gql`
  query GET_CONTENT_BLOCKS(
    $searchTerm: String
    $take: Int
    $skip: Int
    $orderBy: [ContentBlockOrderByWithRelationInput!]
  ) {
    findManyContentBlock(
      where: {
        content_block_profiles: {
          some: { name: { contains: $searchTerm, mode: insensitive } }
        }
      }
      take: $take
      skip: $skip
      orderBy: $orderBy
    ) {
      id
      type
      content_block_profiles {
        id
        name
        language
      }
      page {
        id
        code
        page_profiles {
          title
          language
        }
      }
      product_containers {
        id
      }
      createdAt
      updatedAt
    }
    findManyContentBlockCount(
      where: {
        content_block_profiles: {
          some: { name: { contains: $searchTerm, mode: insensitive } }
        }
      }
    )
  }
`

export const GET_CONTENT_BLOCK = gql`
  query GetContentBlock($id: Int!) {
    findUniqueContentBlock(where: { id: $id }) {
      id
      type
      style
      video_link
      video_type
      extra_css
      text_color
      background_color
      main_image {
        id
        src
      }
      gallery_images {
        id
        src
        display_order
      }
      link_new_tab
      link_page_id
      link_page {
        id
        code
        page_profiles {
          title
          language
        }
      }
      cta_new_tab
      cta_link_page_id
      cta_link_page {
        id
      }
      cta_link_type
      cta_link_category {
        id
      }
      content_block_profiles {
        id
        name
        description
        description_format
        link
        cta_label
        cta_link
        language
      }
      grid_product_containers {
        id
      }
      linked_items {
        id
        title
        link
        arrangement
        image {
          id
          src
        }
      }
      config
      categories {
        id
        image { id src }
        category_profiles {
          id name language
          meta_information { permalink }
        }
      }
    }
  }
`

// Dedicated "Used on" query for the ContentBlockUsages panel shown under the
// Update button on both edit forms (drawer + modal). Deliberately SEPARATE
// from GET_CONTENT_BLOCK: it selects `categories`/`collections`/
// `shopsettings_home_page_content_blocks`, which the CUSTOM mutations' fixed
// `include` doesn't cover (see CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT's doc)
// and which aren't needed by the rest of the form. Also intentionally NOT
// merged into GET_CONTENT_BLOCK itself — both forms' population effects
// `form.reset()` whenever that query's cache entry changes identity, and this
// query's narrower per-relation selections (no `id` on every nested object in
// every case) could otherwise clobber sibling fields normalized onto the same
// `ContentBlock:<id>` cache entry. The component using this query runs it
// with `fetchPolicy: 'no-cache'` so it never writes into the normalized cache
// at all.
export const GET_CONTENT_BLOCK_USAGES = gql`
  query GetContentBlockUsages($id: Int!) {
    findUniqueContentBlock(where: { id: $id }) {
      id
      type
      page {
        id
        code
        page_profiles {
          title
          language
        }
      }
      categories {
        id
        category_profiles {
          name
          language
        }
      }
      collections {
        id
        collection_profiles {
          name
          language
        }
      }
      product_containers {
        id
        reference
        advanced_product {
          advanced_product_profiles {
            name
            language
          }
        }
        single_product {
          product_profiles {
            name
            language
          }
        }
      }
      grid_product_containers {
        id
      }
      shopsettings_home_page_content_blocks {
        id
      }
    }
  }
`

// Resolves display names for a `product_grid` block's picks
// (`grid_product_containers`, ids only, in order) for the "Products in this
// grid" group in ContentBlockUsages. Separate query because
// `grid_product_containers` is a hand-written field (see
// `ContentBlockGridProductFields` in the backend) whose nested relations
// (single_product/advanced_product) don't resolve on that path — only `id` is
// reliable there. This goes through the standard `product_containers`-style
// relation resolution instead, which DOES resolve nested profiles.
export const GET_PRODUCT_CONTAINERS_BY_IDS = gql`
  query GetProductContainersByIds($ids: [Int!]!) {
    findManyProductContainer(where: { id: { in: $ids } }) {
      id
      reference
      advanced_product {
        advanced_product_profiles {
          name
          language
        }
      }
      single_product {
        product_profiles {
          name
          language
        }
      }
    }
  }
`

export const CREATE_CONTENT_BLOCK = gql`
  ${CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT}
  mutation CREATE_CONTENT_BLOCK(
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $video_link: String
    $video_type: ContentBlockVideoType
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $config: Json
    $link_new_tab: Boolean
    $link_page_id: String
    $cta: CtaLinkInput
  ) {
    createNewContentBlock(
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      video_link: $video_link
      video_type: $video_type
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      config: $config
      link_new_tab: $link_new_tab
      link_page_id: $link_page_id
      cta: $cta
    ) {
      ...ContentBlockMutationResult
    }
  }
`

export const CREATE_BUTTON_CONTENT_BLOCK = gql`
  mutation CREATE_BUTTON_CONTENT_BLOCK(
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $video_link: String
    $video_type: ContentBlockVideoType
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $link_new_tab: Boolean!
    $link_page_id: String
  ) {
    createNewContentBlock(
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      video_link: $video_link
      video_type: $video_type
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      link_new_tab: $link_new_tab
      link_page_id: $link_page_id
    ) {
      id
      type
      style
      content_block_profiles {
        id
        name
        language
      }
    }
  }
`

export const UPDATE_CONTENT_BLOCK = gql`
  ${CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT}
  mutation UPDATE_CONTENT_BLOCK(
    $where: ContentBlockWhereUniqueInput!
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $video_link: String
    $video_type: ContentBlockVideoType
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $config: Json
    $link_new_tab: Boolean
    $link_page_id: String
    $cta: CtaLinkInput
  ) {
    updateExistingContentBlock(
      where: $where
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      video_link: $video_link
      video_type: $video_type
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      config: $config
      link_new_tab: $link_new_tab
      link_page_id: $link_page_id
      cta: $cta
    ) {
      ...ContentBlockMutationResult
    }
  }
`

export const UPDATE_BUTTON_CONTENT_BLOCK = gql`
  mutation UPDATE_BUTTON_CONTENT_BLOCK(
    $where: ContentBlockWhereUniqueInput!
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $video_link: String
    $video_type: ContentBlockVideoType
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $link_new_tab: Boolean!
    $link_page_id: String
  ) {
    updateExistingContentBlock(
      where: $where
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      video_link: $video_link
      video_type: $video_type
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      link_new_tab: $link_new_tab
      link_page_id: $link_page_id
    ) {
      id
      type
      style
      video_link
      video_type
      extra_css
      text_color
      background_color
      main_image {
        id
        src
      }
      gallery_images {
        id
        src
      }
      content_block_profiles {
        id
        name
        description
        description_format
        link
        language
      }
      product_containers {
        id
      }
    }
  }
`

export const DELETE_MANY_CONTENT_BLOCKS = gql`
  mutation DELETE_CONTENT_BLOCKS($ids: [Int!]!) {
    deleteManyContentBlock(where: { id: { in: $ids } }) {
      count
    }
  }
`

export const GET_ALL_CONTENT_BLOCKS = gql`
  ${CONTENT_BLOCK_PREVIEW_FRAGMENT}
  query GET_ALL_CONTENT_BLOCKS($searchTerm: String) {
    findManyContentBlock(
      where: {
        content_block_profiles: {
          some: { name: { contains: $searchTerm, mode: insensitive } }
        }
      }
      take: 50
      orderBy: [{ createdAt: desc }]
    ) {
      ...ContentBlockPreviewFields
      pageId
      page {
        id
        code
        page_profiles {
          title
          language
        }
      }
    }
  }
`

export const UPDATE_CONTENT_BLOCK_ARRANGEMENT = gql`
  mutation UPDATE_CONTENT_BLOCK_ARRANGEMENT(
    $where: ContentBlockWhereUniqueInput!
    $data: ContentBlockUpdateInput!
  ) {
    updateOneContentBlock(where: $where, data: $data) {
      id
      arrangement
    }
  }
`

// Inline description edit (full-size page-editor preview).
// Touches ONLY one profile's description via the generic Prisma passthrough
// mutation — nothing else on the block is sent, so other fields can't be
// nulled. Update path edits an existing profile; create path adds a profile
// for a language that has none yet (name is required by the schema).
export const UPDATE_CONTENT_BLOCK_PROFILE = gql`
  mutation UPDATE_CONTENT_BLOCK_PROFILE(
    $where: ContentBlockWhereUniqueInput!
    $data: ContentBlockUpdateInput!
  ) {
    updateOneContentBlock(where: $where, data: $data) {
      id
      content_block_profiles {
        id
        language
        description
        description_format
        name
      }
    }
  }
`

export const CREATE_LINKED_CONTENT_BLOCK = gql`
  ${CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT}
  mutation CREATE_LINKED_CONTENT_BLOCK(
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $grid_products: [Int!]
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $linked_items: [ContentBlockLinkedItemInput]
    $config: Json
  ) {
    createNewContentBlock(
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      grid_products: $grid_products
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      linked_items: $linked_items
      config: $config
    ) {
      ...ContentBlockMutationResult
    }
  }
`

export const UPDATE_LINKED_CONTENT_BLOCK = gql`
  ${CONTENT_BLOCK_MUTATION_RESULT_FRAGMENT}
  mutation UPDATE_LINKED_CONTENT_BLOCK(
    $where: ContentBlockWhereUniqueInput!
    $content_block_profiles: [ContentBlockProfileCustomInput!]!
    $content_block_type: ContentBlockType!
    $style: String
    $images: [String]
    $gallery_images: [String]
    $selected_products: [ProductContainerWhereUniqueInput]
    $grid_products: [Int!]
    $video_autoplay: Boolean!
    $video_loop: Boolean!
    $default_margins: Boolean!
    $object_fit_cover: Boolean!
    $extra_css: Json
    $text_color: String
    $background_color: String
    $linked_items: [ContentBlockLinkedItemInput]
    $config: Json
  ) {
    updateExistingContentBlock(
      where: $where
      content_block_profiles: $content_block_profiles
      content_block_type: $content_block_type
      style: $style
      images: $images
      gallery_images: $gallery_images
      selected_products: $selected_products
      grid_products: $grid_products
      video_autoplay: $video_autoplay
      video_loop: $video_loop
      default_margins: $default_margins
      object_fit_cover: $object_fit_cover
      extra_css: $extra_css
      text_color: $text_color
      background_color: $background_color
      linked_items: $linked_items
      config: $config
    ) {
      ...ContentBlockMutationResult
    }
  }
`
